// @flow
import * as React from 'react';
import styled, {keyframes} from 'styled-components';
import { ButtonIconInDivS } from 'components/ButtonIcon';
import {MODE, ButtonSizeL, ConfiguratorBottom} from 'components/Constants';
import {useSessionState} from 'components/Session';
import {LocalText} from 'components/Localized';
import {showExterior, showInterior} from 'utils/vrUtils';
import CarOption from './CarOptions';

type Props = {
  onInfo: (string) => void,
  onFullscreen: (boolean) => void,
  fullscreen: boolean,
  selectedInfo: boolean,
  openSidebar: boolean,
};

export default function Configurator(props: Props): React.Element<any> {
  const sessionState = useSessionState();
  const modes = React.useMemo(() => [MODE.EXTERIOR, MODE.INTERIOR], []);
  const [mode, setMode] = React.useState(MODE.EXTERIOR);
  const [modeLoading, setModeLoading] = React.useState(false);

  const handleMode = React.useCallback(
    (idx: number) => {
      if (idx <= 1) {
        setMode(modes[idx]);
      }
      if (idx === MODE.EXTERIOR) {
        showExterior(sessionState.options);
        setModeLoading(true);
        setTimeout(() => setModeLoading(false), 2000);
      } else if (idx === MODE.INTERIOR) {
        showInterior(sessionState.options);
        setModeLoading(true);
        setTimeout(() => setModeLoading(false), 2000);
      }
      //handleOption(CAR.NONE);
      props.onInfo('');
    },
    [props, modes, sessionState.options]
  );

  const handleFullscreen = React.useCallback(
    (flag: boolean) => {
      props.onFullscreen(flag);
    },
    [props]
  );

  return (
    <Container>
      <DivFill />
      {/* <IsMobileAgent>
        <DivSafearea>
          <CarOption
            mode={mode}
            hidden={props.fullscreen || props.openSidebar}
            onInfo={props.onInfo}
            selectedInfo={props.selectedInfo}
          />
        </DivSafearea>
      </IsMobileAgent> */}
      <Dim dataFull={props.fullscreen} />
      <Wrapper>
        <div>
          <ButtonWrapper dataHidden={props.openSidebar} dataFull={props.fullscreen}>
            <ModeButton
              dataHidden={props.openSidebar}
              source={
                mode === MODE.INTERIOR
                  ? './icons/btn_exterior.svg'
                  : './icons/btn_interior.svg'
              }
              sourceMobile={
                mode === MODE.INTERIOR
                  ? './icons/btn_exterior.svg'
                  : './icons/btn_interior.svg'
              }
              onClick={
                mode === MODE.INTERIOR
                  ? () => handleMode(MODE.EXTERIOR)
                  : () => handleMode(MODE.INTERIOR)
              }
            />
          </ButtonWrapper>
        </div>
        <WarnText>
          <LocalText textKey="vehicles_warn_message2" />
        </WarnText>
        <div>
          <FullscreenButton
            dataHidden={props.openSidebar}
            source={
              props.fullscreen
                ? './icons/btn_fullscreen_off.svg'
                : './icons/btn_fullscreen_on.svg'
            }
            sourceMobile={
              props.fullscreen
                ? './icons/btn_fullscreen_off.svg'
                : './icons/btn_fullscreen_on.svg'
            }
            onClick={() => handleFullscreen(!props.fullscreen)}
          />
        </div>
      </Wrapper>
      {/* <IsDesktopAgent>
        <CarOptionsWrapper>
          <CarOption
            mode={mode}
            hidden={props.fullscreen || props.openSidebar}
            onInfo={props.onInfo}
            selectedInfo={props.selectedInfo}
          />
        </CarOptionsWrapper>
      </IsDesktopAgent> */}
      <CarOptionsWrapper dataFull={props.fullscreen} dataModeLoading={modeLoading}>
        <CarOption
          mode={mode}
          hidden={props.fullscreen || props.openSidebar}
          onInfo={props.onInfo}
          onChangeMode={handleMode}
          selectedInfo={props.selectedInfo}
        />
      </CarOptionsWrapper>
    </Container>
  );
}

const fullscreenOnAnimation = keyframes`
  0% { transform: translateY(0px); opacity: 1; }
  100% { transform: translateY(100px); opacity: 0;}
`

const fullscreenOffAnimation = keyframes`
  0% { transform: translateY(100px); opacity: 0;}
  100% { transform: translateY(0px); opacity: 1;}
`

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-end;
`;

const WarnText = styled.div`
  position: relative;
  flex: 1;
  margin-bottom: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 20 : 20)}px;
  text-align: center;
`

const Dim = styled.div`
  position: absolute;
  left: 0px;
  right: 0px;
  bottom: 0px;
  height: ${(props: any) => props.theme.layout.sizeS ? '180px' : (props.theme.layout.sizeM ? '118px' : '300px')};

  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 1));

  opacity: ${(props: any) => props.dataFull ? 0 : 1};
  transition: opacity 0.25s;
`

const DivFill = styled.div`
  width: 100%;
  flex: 1;
`;

const ButtonWrapper = styled.div`
  height: ${(props: any) =>
    props.theme.layout.scale * (!props.theme.layout.mobile ? ConfiguratorBottom.desktop + ConfiguratorBottom.desktop + ButtonSizeL.desktop : ConfiguratorBottom.mobile + ConfiguratorBottom.mobile + ButtonSizeL.mobile)}px;
  margin-left: ${(props: any) =>
    props.theme.layout.scale * (!props.theme.layout.mobile ? 40 : 20)}px;

  display: flex;
  align-items: center;
  justify-content: flex-start;

  opacity: ${(props: any) => (props.dataHidden ? 0 : undefined)};
  transition: opacity ${(props: any) => props.theme.defaults.transitionDuration}s;
  > * {
    pointer-events: ${(props: any) => props.dataHidden ? 'none' : undefined};
  }

  > :first-child {
    margin-right: ${(props: any) => (!props.theme.layout.mobile ? 16 : 12)}px;
  }

  animation-name: ${(props: any) => props.dataFull === undefined ? 'undefind' : (props.dataFull ? fullscreenOnAnimation : fullscreenOffAnimation)};
  animation-duration: 0.25s;
`;

const ModeButton = styled(ButtonIconInDivS)`
  height: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 48 : 36)}px;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  visibility: hidden;
`;

const FullscreenButton = styled(ButtonIconInDivS)`
  position: relative;
  height: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? ConfiguratorBottom.desktop + ConfiguratorBottom.desktop + ButtonSizeL.desktop : ConfiguratorBottom.mobile + ConfiguratorBottom.mobile + ButtonSizeL.mobile)}px;
  margin-right: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? 40 : 20)}px;

  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const CarOptionsWrapper = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  padding-bottom: ${(props: any) => props.theme.layout.scale * (!props.theme.layout.mobile ? ConfiguratorBottom.desktop : ConfiguratorBottom.mobile)}px;
  box-sizing: border-box;

  display: flex;
  justify-content: flex-end;
  flex-direction: column;

  > div {
    opacity: ${(props: any) => props.dataModeLoading ? 0 : 1};
  }

  animation-name: ${(props: any) => props.dataFull === undefined ? 'undefind' : (props.dataFull ? fullscreenOnAnimation : fullscreenOffAnimation)};
  animation-duration: 0.25s;
`
